import * as RadixToast from '@radix-ui/react-toast';
import { X } from 'lucide-react';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { Icon } from '../Icon/Icon';
import s from './Toast.module.css';

export type ToastOptions = {
  title: ReactNode;
  description?: ReactNode;
  /** e.g. { label: 'Undo', onAction: () => … }. Toasts with an action stay 6s instead of 4s. */
  action?: { label: string; onAction: () => void };
  /** A leading emoji; decorative. */
  emoji?: string;
  duration?: number;
};

type ToastItem = ToastOptions & { id: number };

const ToastContext = createContext<((t: ToastOptions) => void) | null>(null);

/** Show a toast: `const toast = useToast(); toast({ title: 'Claimed! 🎁', action: { label: 'Undo', onAction } })`. */
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

let nextId = 1;

export type ToastProviderProps = {
  children: ReactNode;
  /** Phones: sit above the bottom nav (default) or at the screen bottom (screens without the nav). */
  placement?: 'aboveNav' | 'bottom';
};

export function ToastProvider({ children, placement = 'aboveNav' }: ToastProviderProps) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const show = useCallback(
    (t: ToastOptions) => setItems((cur) => [...cur.slice(-2), { ...t, id: nextId++ }]),
    [],
  );
  const remove = (id: number) => setItems((cur) => cur.filter((t) => t.id !== id));
  const value = useMemo(() => show, [show]);

  return (
    <ToastContext.Provider value={value}>
      <RadixToast.Provider swipeDirection="down" label="Notification">
        {children}
        {items.map((t) => (
          <RadixToast.Root
            key={t.id}
            className={s.toast}
            duration={t.duration ?? (t.action ? 6000 : 4000)}
            onOpenChange={(open) => {
              if (!open) remove(t.id);
            }}
          >
            {t.emoji ? (
              <span className={s.emoji} aria-hidden="true">
                {t.emoji}
              </span>
            ) : null}
            <div className={s.text}>
              <RadixToast.Title className={s.title}>{t.title}</RadixToast.Title>
              {t.description ? (
                <RadixToast.Description className={s.description}>
                  {t.description}
                </RadixToast.Description>
              ) : null}
            </div>
            {t.action ? (
              <RadixToast.Action asChild altText={t.action.label}>
                <button type="button" className={s.action} onClick={t.action.onAction}>
                  {t.action.label}
                </button>
              </RadixToast.Action>
            ) : null}
            <RadixToast.Close className={s.close} aria-label="Dismiss">
              <Icon icon={X} size="sm" />
            </RadixToast.Close>
          </RadixToast.Root>
        ))}
        <RadixToast.Viewport className={s.viewport} data-placement={placement} />
      </RadixToast.Provider>
    </ToastContext.Provider>
  );
}
